import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  VStack,
  HStack,
  Text,
  Input,
  Textarea,
  NativeSelect,
  Card,
  Heading,
  Separator,
  Badge,
  Grid,
  GridItem,
  Switch,
  Field,
} from '@chakra-ui/react';
import { Accordion } from '@chakra-ui/react';
import { DialogRoot, DialogContent, DialogHeader, DialogBody, DialogFooter, DialogBackdrop, DialogCloseTrigger } from '@chakra-ui/react';
import { Dialog } from '@chakra-ui/react';
import { Tabs } from '@chakra-ui/react';
import { Alert } from '@chakra-ui/react';
import { FiUpload, FiSettings, FiInfo, FiTrash2 } from 'react-icons/fi';
import api from '../services/api';
import { toaster } from './ui/toaster';
import { useTranslation } from 'react-i18next';
import { getAssessmentUnitLabels } from '../utils/frameworkTerminology';

interface StandardTemplate {
  standard_id: string;
  name: string;
  category: string;
  assessment_type: string;
  compliance_criteria: Record<string, any>;
  scoring_method: string;
  report_template: string;
  prompt_template: string;
  ui_guidance: string;
  file_requirements: string[];
  help_text: string;
}

interface StandardConfig {
  standard_id: string;
  name: string;
  version: string;
  category: string;
  classification: string;
  assessment_type: string;
  compliance_criteria: Record<string, any>;
  scoring_method: string;
  report_template: string;
  prompt_template: string;
  ai_model_config: Record<string, any>;
  ui_guidance: string;
  file_requirements: string[];
  help_text: string;
  description: string;
  official_url?: string;
  is_active: boolean;
}

// Edit Standard Form Component
const EditStandardForm: React.FC<{
  standard: any;
  onUpdate: (data: any) => void;
  onCancel: () => void;
  templates: Record<string, any>;
}> = ({ standard, onUpdate, onCancel, templates }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: standard.name || '',
    version: standard.version || '',
    description: standard.description || '',
    category: standard.category || 'Information Security',
    classification: standard.classification || '',
    assessment_type: standard.assessment_type || 'compliance_audit',
    compliance_criteria: standard.compliance_criteria || {},
    scoring_method: standard.scoring_method || 'weighted_compliance',
    report_template: standard.report_template || 'iso27001_comprehensive',
    review_prompt_template: standard.review_prompt_template || '',
    ui_guidance: standard.ui_guidance || '',
    file_requirements: standard.file_requirements || ['PDF', 'DOCX', 'TXT'],
    help_text: standard.help_text || '',
    official_url: standard.official_url || '',
    is_active: standard.is_active ?? true,
    ai_model_config: standard.ai_model_config || {
      model: '', // Leave blank so backend uses OLLAMA_MODEL
      temperature: 0.3
    }
  });
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTemplateSelect = (templateKey: string) => {
    setSelectedTemplate(templateKey);
    const template = templates[templateKey];
    if (template) {
      setFormData(prev => ({
        ...prev,
        name: template.name,
        category: template.category,
        assessment_type: template.assessment_type,
        compliance_criteria: template.compliance_criteria,
        scoring_method: template.scoring_method,
        report_template: template.report_template,
        review_prompt_template: template.prompt_template,
        ui_guidance: template.ui_guidance,
        file_requirements: template.file_requirements,
        help_text: template.help_text
      }));
    }
  };

  const handleSubmit = () => {
    onUpdate(formData);
  };

  return (
    <VStack gap={6} align="stretch">
      {/* Basic Configuration */}
      <Box>
        <Heading size="sm" mb={4}>{t('createReview.basicConfiguration')}</Heading>
        <Grid templateColumns="repeat(2, 1fr)" gap={4}>
          <Field.Root required>
            <Field.Label>{t('createReview.standardName')}</Field.Label>
            <Input
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder={t('createReview.standardNamePlaceholder')}
            />
          </Field.Root>

          <Field.Root>
            <Field.Label>{t('createReview.version')}</Field.Label>
            <Input
              value={formData.version}
              onChange={(e) => handleChange('version', e.target.value)}
              placeholder={t('createReview.version')}
            />
          </Field.Root>

          <Field.Root>
            <Field.Label>{t('createReview.category')}</Field.Label>
            <NativeSelect.Root>
              <NativeSelect.Field
                value={formData.category}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleChange('category', e.target.value)}
                placeholder={t('createReview.selectCategory')}
              >
                <option value="Information Security">{t('createReview.informationSecurity')}</option>
                <option value="Trust Services">{t('createReview.trustServices')}</option>
                <option value="Cybersecurity">{t('createReview.cybersecurity')}</option>
                <option value="Compliance">{t('createReview.compliance')}</option>
                <option value="Risk Management">{t('createReview.riskManagement')}</option>
              </NativeSelect.Field>
            </NativeSelect.Root>
          </Field.Root>

          <Field.Root>
            <Field.Label>{t('createReview.classification')}</Field.Label>
            <NativeSelect.Root>
              <NativeSelect.Field
                value={formData.classification}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleChange('classification', e.target.value)}
                placeholder={t('createReview.selectClassification')}
              >
                <option value="">{t('createReview.notClassified')}</option>
                <option value="Standard">{t('createReview.standard')}</option>
                <option value="Framework">{t('createReview.framework')}</option>
                <option value="Regulation">{t('createReview.regulation')}</option>
              </NativeSelect.Field>
            </NativeSelect.Root>
          </Field.Root>

          <Field.Root>
            <Field.Label>{t('createReview.officialUrlOptional')}</Field.Label>
            <Input
              value={formData.official_url}
              onChange={(e) => handleChange('official_url', e.target.value)}
              placeholder="https://example.com/standard"
            />
          </Field.Root>

          <GridItem colSpan={2}>
            <Field.Root>
              <Field.Label>{t('createReview.description')}</Field.Label>
              <Textarea
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder={t('createReview.standardDescriptionPlaceholder')}
                rows={3}
              />
            </Field.Root>
          </GridItem>
        </Grid>
      </Box>

      {/* Assessment Configuration */}
      <Box>
        <Heading size="sm" mb={4}>{t('createReview.assessmentConfiguration')}</Heading>
        <VStack gap={4} align="stretch">
          <Field.Root required>
            <Field.Label>{t('createReview.assessmentType')}</Field.Label>
            <NativeSelect.Root>
              <NativeSelect.Field
                value={formData.assessment_type}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleChange('assessment_type', e.target.value)}
                placeholder={t('createReview.selectAssessmentType')}
              >
                <option value="compliance_audit">{t('createReview.complianceAudit')}</option>
                <option value="trust_criteria">{t('createReview.trustCriteria')}</option>
                <option value="framework_alignment">{t('createReview.frameworkAlignment')}</option>
                <option value="risk_assessment">{t('createReview.riskAssessment')}</option>
                <option value="maturity_evaluation">{t('createReview.maturityEvaluation')}</option>
              </NativeSelect.Field>
            </NativeSelect.Root>
          </Field.Root>

          <Field.Root>
            <Field.Label>{t('createReview.scoringMethod')}</Field.Label>
            <NativeSelect.Root>
              <NativeSelect.Field
                value={formData.scoring_method}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleChange('scoring_method', e.target.value)}
                placeholder={t('createReview.selectScoringMethod')}
              >
                <option value="weighted_compliance">{t('createReview.weightedCompliance')}</option>
                <option value="observation_ranking">{t('createReview.observationRanking')}</option>
                <option value="percentage_score">{t('createReview.percentageScore')}</option>
                <option value="maturity_assessment">{t('createReview.maturityAssessment')}</option>
                <option value="trust_services_evaluation">{t('createReview.trustServicesEvaluation')}</option>
                <option value="framework_alignment">{t('createReview.frameworkAlignment')}</option>
              </NativeSelect.Field>
            </NativeSelect.Root>
          </Field.Root>

          <Field.Root>
            <Field.Label>{t('createReview.reportStyle')}</Field.Label>
            <NativeSelect.Root>
              <NativeSelect.Field
                value={formData.report_template}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleChange('report_template', e.target.value)}
                placeholder={t('createReview.selectReportStyle')}
              >
                <option value="iso27001_comprehensive">{t('createReview.iso27001Comprehensive')}</option>
                <option value="soc2_assessment">{t('createReview.soc2Assessment')}</option>
                <option value="nist_csf_maturity">{t('createReview.nistCsfMaturity')}</option>
                <option value="custom_iso_style">{t('createReview.customIsoStyle')}</option>
                <option value="executive_summary">{t('createReview.executiveSummary')}</option>
                <option value="detailed_technical">{t('createReview.detailedTechnical')}</option>
              </NativeSelect.Field>
            </NativeSelect.Root>
          </Field.Root>

          <Field.Root>
            <Field.Label>{t('createReview.complianceCriteria')}</Field.Label>
            <Textarea
              value={JSON.stringify(formData.compliance_criteria, null, 2)}
              onChange={(e) => {
                try {
                  const parsed = JSON.parse(e.target.value);
                  handleChange('compliance_criteria', parsed);
                } catch (error) {
                  // Allow invalid JSON during typing
                }
              }}
              placeholder='{"observation_rankings": ["Major Non-Conformity", "Minor Non-Conformity", "Conforms", "Cannot be Assessed"]}'
              rows={4}
              fontFamily="mono"
            />
            <Field.HelperText>
              {t('createReview.complianceCriteriaJsonHelp')}
            </Field.HelperText>
          </Field.Root>
        </VStack>
      </Box>

      {/* AI Configuration */}
      <Box>
        <Heading size="sm" mb={4}>{t('createReview.aiConfiguration')}</Heading>
        <VStack gap={4} align="stretch">
          <Field.Root>
            <Field.Label>{t('createReview.reviewPromptTemplate')}</Field.Label>
            <Textarea
              value={formData.review_prompt_template}
              onChange={(e) => handleChange('review_prompt_template', e.target.value)}
              placeholder={t('createReview.promptTemplatePlaceholder')}
              rows={6}
            />
            <Field.HelperText>
              {t('createReview.aiPromptTemplateHelp')}
            </Field.HelperText>
          </Field.Root>

          <Grid templateColumns="repeat(3, 1fr)" gap={4}>
            <Field.Root>
              <Field.Label>{t('createReview.aiModel')}</Field.Label>
              <Input
                value={formData.ai_model_config.model}
                onChange={(e) => handleChange('ai_model_config', {
                  ...formData.ai_model_config,
                  model: e.target.value
                })}
                placeholder={t('createReview.defaultFromBackend')}
              />
            </Field.Root>
            <Field.Root>
              <Field.Label>{t('createReview.temperature')}</Field.Label>
              <Input
                type="number"
                step="0.1"
                min="0"
                max="2"
                value={formData.ai_model_config.temperature}
                onChange={(e) => handleChange('ai_model_config', {
                  ...formData.ai_model_config,
                  temperature: parseFloat(e.target.value)
                })}
              />
            </Field.Root>
          </Grid>
        </VStack>
      </Box>

      {/* UI Configuration */}
      <Box>
        <Heading size="sm" mb={4}>{t('createReview.uiConfiguration')}</Heading>
        <VStack gap={4} align="stretch">
          <Field.Root>
            <Field.Label>{t('createReview.uiGuidance')}</Field.Label>
            <Textarea
              value={formData.ui_guidance}
              onChange={(e) => handleChange('ui_guidance', e.target.value)}
              placeholder={t('createReview.uiGuidancePlaceholder')}
              rows={3}
            />
          </Field.Root>

          <Field.Root>
            <Field.Label>{t('createReview.helpText')}</Field.Label>
            <Textarea
              value={formData.help_text}
              onChange={(e) => handleChange('help_text', e.target.value)}
              placeholder={t('createReview.helpTextPlaceholder')}
              rows={4}
            />
          </Field.Root>

          <Field.Root>
            <Field.Label>{t('createReview.fileRequirements')}</Field.Label>
            <Input
              value={formData.file_requirements?.join(', ') || ''}
              onChange={(e) => handleChange('file_requirements', e.target.value.split(', ').map(s => s.trim()))}
              placeholder="PDF, DOCX, TXT"
            />
          </Field.Root>

          <Field.Root>
            <HStack gap={3}>
              <Field.Label htmlFor="active-standard-edit" mb="0">
                {t('createReview.activeStandard')}
              </Field.Label>
              <Switch.Root
                id="active-standard-edit"
                checked={Boolean(formData.is_active)}
                onCheckedChange={(details) => {
                  handleChange('is_active', details.checked);
                }}
              >
                <Switch.HiddenInput />
                <Switch.Control />
              </Switch.Root>
            </HStack>
            <Field.HelperText>
              {t('createReview.inactiveStandardsHiddenFromUsersList')}
            </Field.HelperText>
          </Field.Root>
        </VStack>
      </Box>

      {/* Template Selector */}
      <Field.Root>
        <Field.Label>{t('createReview.applyTemplate')}</Field.Label>
        <NativeSelect.Root>
          <NativeSelect.Field
            value={selectedTemplate}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleTemplateSelect(e.target.value)}
            placeholder={t('createReview.selectTemplateAutoFillSettings')}
          >
            {Object.entries(templates).map(([key, template]) => (
              <option key={key} value={key}>{template.name}</option>
            ))}
          </NativeSelect.Field>
        </NativeSelect.Root>
        <Field.HelperText>
          {t('createReview.templateAutoFillHelp')}
        </Field.HelperText>
      </Field.Root>

      <HStack gap={3} justify="flex-end">
        <Button variant="outline" onClick={onCancel}>
          {t('createReview.cancel')}
        </Button>
        <Button colorPalette="blue" onClick={handleSubmit}>
          {t('createReview.updateStandard')}
        </Button>
      </HStack>
    </VStack>
  );
};

const MultiStandardUpload: React.FC = () => {
  const { t, i18n } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [templates, setTemplates] = useState<Record<string, StandardTemplate>>({});
  const [categories, setCategories] = useState<string[]>([]);
  const [assessmentTypes, setAssessmentTypes] = useState<string[]>([]);
  const [existingStandards, setExistingStandards] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [editingStandard, setEditingStandard] = useState<any>(null);
  const [deletingStandard, setDeletingStandard] = useState<any>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  
  // Form state
  const [config, setConfig] = useState<StandardConfig>({
    standard_id: '',
    name: '',
    version: '1.0',
    category: '',
    classification: '',
    assessment_type: '',
    compliance_criteria: {},
    scoring_method: 'custom',
    report_template: 'custom',
    prompt_template: '',
    ai_model_config: {
      model: '', // Use backend/environment default
      temperature: 0.3
    },
    ui_guidance: '',
    file_requirements: ['PDF', 'DOCX', 'TXT'],
    help_text: '',
    description: '',
    official_url: '',
    is_active: true
  });

  useEffect(() => {
    fetchTemplates();
    fetchExistingStandards();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await api.get('/api/admin/multi-standards/templates');
      const data = response.data;
      setTemplates(data.templates);
      setCategories(data.categories);
      setAssessmentTypes(data.assessment_types);
    } catch (error: any) {
      console.error('Failed to fetch templates:', error?.response?.status, error?.message);
    }
  };

  const fetchExistingStandards = async () => {
    try {
      const response = await api.get('/api/admin/multi-standards/existing');
      setExistingStandards(response.data.existing_standards || []);
    } catch (error: any) {
      console.error('Failed to fetch existing standards:', error?.response?.status, error?.message);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleTemplateSelect = (templateKey: string) => {
    setSelectedTemplate(templateKey);
    const template = templates[templateKey];
    
    if (template) {
      setConfig(prev => ({
        ...prev,
        standard_id: template.standard_id,
        name: template.name,
        category: template.category,
        assessment_type: template.assessment_type,
        compliance_criteria: template.compliance_criteria,
        scoring_method: template.scoring_method,
        report_template: template.report_template,
        prompt_template: template.prompt_template,
        ui_guidance: template.ui_guidance,
        file_requirements: template.file_requirements,
        help_text: template.help_text
      }));
    }
  };

  const handleConfigChange = (field: keyof StandardConfig, value: any) => {
    setConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleUpload = async () => {
    if (!selectedFile || !config.standard_id || !config.name) {
      toaster.create({
        title: t('createReview.missingInformation'),
        description: t('createReview.selectFileAndStandardConfiguration'),
        type: 'error',
        duration: 5000,
      });
      return;
    }

    setIsLoading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('standard_config', JSON.stringify(config));

      const response = await api.post('/api/admin/multi-standards/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.status === 200) {
        const result = response.data;
        toaster.create({
          title: t('createReview.success'),
          description: t('createReview.successfullyImportedSections', { name: config.name, count: result.sections_imported }),
          type: 'success',
          duration: 5000,
        });
        
        // Reset form
        setSelectedFile(null);
        setSelectedTemplate('');
        setConfig({
          standard_id: '',
          name: '',
          version: '1.0',
          category: '',
          classification: '',
          assessment_type: '',
          compliance_criteria: {},
          scoring_method: 'custom',
          report_template: 'custom',
          prompt_template: '',
          ai_model_config: {
            model: '', // Use backend/environment default
            temperature: 0.3
          },
          ui_guidance: '',
          file_requirements: ['PDF', 'DOCX', 'TXT'],
          help_text: '',
          description: '',
          official_url: '',
          is_active: true
        });
      }
    } catch (error: any) {
      toaster.create({
        title: t('createReview.uploadFailed'),
        description: error?.response?.data?.detail || error?.message || t('createReview.unknownErrorOccurred'),
        type: 'error',
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getAssessmentTypeDescription = (type: string) => {
    const descriptions = {
      'observation_ranking': t('createReview.observationRankingDescription'),
      'compliance_score': t('createReview.complianceScoreDescription'),
      'maturity_level': t('createReview.maturityLevelDescription'),
      'binary_compliance': t('createReview.binaryComplianceDescription'),
      'custom': t('createReview.customAssessmentMethodology')
    };
    return descriptions[type as keyof typeof descriptions] || t('createReview.customAssessmentType');
  };

  const handleEditStandard = (standard: any) => {
    setEditingStandard(standard);
  };

  const handleUpdateStandard = async (updatedData: any) => {
    try {
      await api.put(`/api/admin/multi-standards/${editingStandard.id}`, updatedData);
      toaster.create({
        title: t('createReview.standardUpdated'),
        description: t('createReview.standardUpdatedSuccessfully', { name: editingStandard.name }),
        type: 'success',
        duration: 3000,
      });
      setEditingStandard(null);
      fetchExistingStandards(); // Refresh the list
    } catch (error: any) {
      toaster.create({
        title: t('createReview.updateFailed'),
        description: error?.response?.data?.detail || t('createReview.failedToUpdateStandardTryAgain'),
        type: 'error',
        duration: 3000,
      });
    }
  };

  const handleDeleteStandard = (standard: any) => {
    setDeletingStandard(standard);
    setIsDeleteModalOpen(true);
  };

  const confirmDeleteStandard = async () => {
    try {
      await api.delete(`/api/admin/multi-standards/${deletingStandard.id}`);
      toaster.create({
        title: t('createReview.standardDeleted'),
        description: t('createReview.standardDeletedSuccessfully', { name: deletingStandard.name }),
        type: 'success',
        duration: 3000,
      });
      setIsDeleteModalOpen(false);
      setDeletingStandard(null);
      fetchExistingStandards(); // Refresh the list
    } catch (error: any) {
      toaster.create({
        title: t('createReview.deleteFailed'),
        description: t('createReview.failedToDeleteStandardTryAgain'),
        type: 'error',
        duration: 3000,
      });
    }
  };

  return (
    <Box p={6}>
      <VStack gap={6} align="stretch">
        <Card.Root>
          <Card.Header>
            <Heading size="md" display="flex" alignItems="center" gap={2}>
              <FiUpload />
              {t('createReview.multiStandardUpload')}
            </Heading>
            <Text color="gray.600" mt={2}>
              {t('createReview.multiStandardUploadDescription')}
            </Text>
          </Card.Header>
          <Card.Body>
            <Tabs.Root variant="enclosed">
              <Tabs.List>
                <Tabs.Trigger value="upload">{t('createReview.uploadNewStandard')}</Tabs.Trigger>
                <Tabs.Trigger value="library">{t('createReview.standardsLibraryCount', { count: existingStandards.length })}</Tabs.Trigger>
              </Tabs.List>

              <Tabs.Content value="upload">
                  <VStack gap={6} align="stretch">
                    {/* File Upload */}
              <Field.Root>
                <Field.Label>{t('createReview.standardFileCsvJsonTxt')}</Field.Label>
                <Box
                  border="2px dashed"
                  borderColor="blue.200"
                  borderRadius="md"
                  p={6}
                  textAlign="center"
                  _hover={{ borderColor: 'blue.300' }}
                  transition="all 0.2s"
                >
                  <Input
                    type="file"
                    accept=".csv,.json,.txt,.docx,.pdf"
                    onChange={handleFileSelect}
                    border="none"
                    p={0}
                    height="auto"
                    _focus={{ boxShadow: 'none' }}
                    _hover={{ boxShadow: 'none' }}
                  />
                  <Text color="gray.500" mt={2} fontSize="sm">
                    {t('createReview.clickBrowseDragDropFile')}
                  </Text>
                </Box>
                {selectedFile && (
                  <Field.HelperText>
                    {t('createReview.selectedFileSize', { name: selectedFile.name, size: (selectedFile.size / 1024).toFixed(1) })}
                  </Field.HelperText>
                )}
              </Field.Root>

              {/* Template Selection */}
              <Field.Root>
                <Field.Label>{t('createReview.chooseTemplateOptional')}</Field.Label>
                <NativeSelect.Root>
                  <NativeSelect.Field
                    value={selectedTemplate}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleTemplateSelect(e.target.value)}
                    placeholder={t('createReview.selectTemplatePrefillConfiguration')}
                  >
                    {Object.entries(templates).map(([key, template]) => (
                      <option key={key} value={key}>
                        {template.name}
                      </option>
                    ))}
                  </NativeSelect.Field>
                </NativeSelect.Root>
                <Field.HelperText>
                  {t('createReview.templatesProvidePreconfiguredSettings')}
                </Field.HelperText>
              </Field.Root>

              <Separator />

              {/* Configuration Form */}
              <Accordion.Root multiple>
                <Accordion.Item value="basic">
                  <Accordion.ItemTrigger>
                    <Box flex="1" textAlign="left">
                      <Heading size="sm">{t('createReview.basicConfiguration')}</Heading>
                    </Box>
                    <Accordion.ItemIndicator />
                  </Accordion.ItemTrigger>
                  <Accordion.ItemContent>
                    <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                      <GridItem>
                        <Field.Root required>
                          <Field.Label>{t('createReview.standardId')}</Field.Label>
                          <Input
                            value={config.standard_id}
                            onChange={(e) => handleConfigChange('standard_id', e.target.value)}
                            placeholder={t('createReview.standardIdPlaceholder')}
                          />
                        </Field.Root>
                      </GridItem>
                      <GridItem>
                        <Field.Root required>
                          <Field.Label>{t('createReview.standardName')}</Field.Label>
                          <Input
                            value={config.name}
                            onChange={(e) => handleConfigChange('name', e.target.value)}
                            placeholder={t('createReview.customStandardNamePlaceholder')}
                          />
                        </Field.Root>
                      </GridItem>
                      <GridItem>
                        <Field.Root>
                          <Field.Label>{t('createReview.version')}</Field.Label>
                          <Input
                            value={config.version}
                            onChange={(e) => handleConfigChange('version', e.target.value)}
                            placeholder="1.0"
                          />
                        </Field.Root>
                      </GridItem>
                      <GridItem>
                        <Field.Root>
                          <Field.Label>{t('createReview.category')}</Field.Label>
                          <NativeSelect.Root>
                            <NativeSelect.Field
                              value={config.category}
                              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleConfigChange('category', e.target.value)}
                              placeholder={t('createReview.selectCategory')}
                            >
                              {categories.map(cat => (
                                <option key={cat} value={cat}>
                                  {cat.replace('_', ' ').toUpperCase()}
                                </option>
                              ))}
                            </NativeSelect.Field>
                          </NativeSelect.Root>
                        </Field.Root>
                      </GridItem>
                      <GridItem>
                        <Field.Root>
                          <Field.Label>{t('createReview.classification')}</Field.Label>
                          <NativeSelect.Root>
                            <NativeSelect.Field
                              value={config.classification}
                              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleConfigChange('classification', e.target.value)}
                              placeholder={t('createReview.selectClassification')}
                            >
                              <option value="">{t('createReview.notClassified')}</option>
                              <option value="Standard">{t('createReview.standard')}</option>
                              <option value="Framework">{t('createReview.framework')}</option>
                              <option value="Regulation">{t('createReview.regulation')}</option>
                            </NativeSelect.Field>
                          </NativeSelect.Root>
                        </Field.Root>
                      </GridItem>
                      <GridItem colSpan={2}>
                        <Field.Root>
                          <Field.Label>{t('createReview.description')}</Field.Label>
                          <Textarea
                            value={config.description}
                            onChange={(e) => handleConfigChange('description', e.target.value)}
                            placeholder={t('createReview.standardDescriptionPlaceholder')}
                            rows={3}
                          />
                        </Field.Root>
                      </GridItem>
                    </Grid>
                  </Accordion.ItemContent>
                </Accordion.Item>

                <Accordion.Item value="assessment">
                  <Accordion.ItemTrigger>
                    <Box flex="1" textAlign="left">
                      <Heading size="sm">{t('createReview.assessmentConfiguration')}</Heading>
                    </Box>
                    <Accordion.ItemIndicator />
                  </Accordion.ItemTrigger>
                  <Accordion.ItemContent>
                    <VStack gap={4} align="stretch">
                      <Field.Root required>
                        <Field.Label>{t('createReview.assessmentType')}</Field.Label>
                        <NativeSelect.Root>
                          <NativeSelect.Field
                            value={config.assessment_type}
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleConfigChange('assessment_type', e.target.value)}
                            placeholder={t('createReview.selectAssessmentType')}
                          >
                            {assessmentTypes.map(type => (
                              <option key={type} value={type}>
                                {type.replace('_', ' ').toUpperCase()}
                              </option>
                            ))}
                          </NativeSelect.Field>
                        </NativeSelect.Root>
                        {config.assessment_type && (
                          <Field.HelperText>
                            {getAssessmentTypeDescription(config.assessment_type)}
                          </Field.HelperText>
                        )}
                      </Field.Root>

                      <Field.Root>
                        <Field.Label>{t('createReview.scoringMethod')}</Field.Label>
                        <NativeSelect.Root>
                          <NativeSelect.Field
                            value={config.scoring_method}
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleConfigChange('scoring_method', e.target.value)}
                            placeholder={t('createReview.selectScoringMethod')}
                          >
                            <option value="weighted_compliance">{t('createReview.weightedCompliance')}</option>
                            <option value="observation_ranking">{t('createReview.observationRanking')}</option>
                            <option value="percentage_score">{t('createReview.percentageScore')}</option>
                            <option value="maturity_assessment">{t('createReview.maturityAssessment')}</option>
                            <option value="trust_services_evaluation">{t('createReview.trustServicesEvaluation')}</option>
                            <option value="framework_alignment">{t('createReview.frameworkAlignment')}</option>
                          </NativeSelect.Field>
                        </NativeSelect.Root>
                        <Field.HelperText>
                          {t('createReview.howComplianceMeasuredScored')}
                        </Field.HelperText>
                      </Field.Root>

                      <Field.Root>
                        <Field.Label>{t('createReview.reportStyle')}</Field.Label>
                        <NativeSelect.Root>
                          <NativeSelect.Field
                            value={config.report_template}
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleConfigChange('report_template', e.target.value)}
                            placeholder={t('createReview.selectReportStyle')}
                          >
                            <option value="iso27001_comprehensive">{t('createReview.iso27001Comprehensive')}</option>
                            <option value="soc2_assessment">{t('createReview.soc2Assessment')}</option>
                            <option value="nist_csf_maturity">{t('createReview.nistCsfMaturity')}</option>
                            <option value="custom_iso_style">{t('createReview.customIsoStyle')}</option>
                            <option value="executive_summary">{t('createReview.executiveSummary')}</option>
                            <option value="detailed_technical">{t('createReview.detailedTechnical')}</option>
                          </NativeSelect.Field>
                        </NativeSelect.Root>
                        <Field.HelperText>
                          {t('createReview.generatedComplianceReportStyleHelp')}
                        </Field.HelperText>
                      </Field.Root>

                      <Field.Root>
                        <Field.Label>{t('createReview.complianceCriteria')}</Field.Label>
                        <Textarea
                          value={JSON.stringify(config.compliance_criteria, null, 2)}
                          onChange={(e) => {
                            try {
                              const parsed = JSON.parse(e.target.value);
                              handleConfigChange('compliance_criteria', parsed);
                            } catch (error) {
                              // Allow invalid JSON during typing
                            }
                          }}
                          placeholder='{"observation_rankings": ["Major Non-Conformity", "Minor Non-Conformity", "Conforms", "Cannot be Assessed"]}'
                          rows={6}
                          fontFamily="mono"
                        />
                        <Field.HelperText>
                          {t('createReview.complianceCriteriaJsonHelp')}
                        </Field.HelperText>
                      </Field.Root>
                    </VStack>
                  </Accordion.ItemContent>
                </Accordion.Item>

                <Accordion.Item value="ai">
                  <Accordion.ItemTrigger>
                    <Box flex="1" textAlign="left">
                      <Heading size="sm">{t('createReview.aiConfiguration')}</Heading>
                    </Box>
                    <Accordion.ItemIndicator />
                  </Accordion.ItemTrigger>
                  <Accordion.ItemContent>
                    <VStack gap={4} align="stretch">
                      <Field.Root>
                        <Field.Label>{t('createReview.promptTemplate')}</Field.Label>
                        <Textarea
                          value={config.prompt_template}
                          onChange={(e) => handleConfigChange('prompt_template', e.target.value)}
                          placeholder={t('createReview.promptTemplatePlaceholder')}
                          rows={8}
                        />
                        <Field.HelperText>
                          {t('createReview.aiPromptTemplateHelp')}
                        </Field.HelperText>
                      </Field.Root>

                      <Grid templateColumns="repeat(3, 1fr)" gap={4}>
                        <Field.Root>
                          <Field.Label>{t('createReview.aiModel')}</Field.Label>
                          <Input
                            value={config.ai_model_config.model}
                            onChange={(e) => handleConfigChange('ai_model_config', {
                              ...config.ai_model_config,
                              model: e.target.value
                            })}
                            placeholder={t('createReview.defaultFromBackend')}
                          />
                        </Field.Root>
                        <Field.Root>
                          <Field.Label>{t('createReview.temperature')}</Field.Label>
                          <Input
                            type="number"
                            step="0.1"
                            min="0"
                            max="2"
                            value={config.ai_model_config.temperature}
                            onChange={(e) => handleConfigChange('ai_model_config', {
                              ...config.ai_model_config,
                              temperature: parseFloat(e.target.value)
                            })}
                          />
                        </Field.Root>
                      </Grid>
                    </VStack>
                  </Accordion.ItemContent>
                </Accordion.Item>

                <Accordion.Item value="ui">
                  <Accordion.ItemTrigger>
                    <Box flex="1" textAlign="left">
                      <Heading size="sm">{t('createReview.uiConfiguration')}</Heading>
                    </Box>
                    <Accordion.ItemIndicator />
                  </Accordion.ItemTrigger>
                  <Accordion.ItemContent>
                    <VStack gap={4} align="stretch">
                      <Field.Root>
                        <Field.Label>{t('createReview.uiGuidance')}</Field.Label>
                        <Textarea
                          value={config.ui_guidance}
                          onChange={(e) => handleConfigChange('ui_guidance', e.target.value)}
                          placeholder={t('createReview.uiGuidancePlaceholder')}
                          rows={3}
                        />
                      </Field.Root>

                      <Field.Root>
                        <Field.Label>{t('createReview.helpText')}</Field.Label>
                        <Textarea
                          value={config.help_text}
                          onChange={(e) => handleConfigChange('help_text', e.target.value)}
                          placeholder={t('createReview.helpTextPlaceholder')}
                          rows={4}
                        />
                      </Field.Root>

                      <Field.Root>
                        <Field.Label>{t('createReview.fileRequirements')}</Field.Label>
                        <Input
                          value={config.file_requirements?.join(', ') || ''}
                          onChange={(e) => handleConfigChange('file_requirements', e.target.value.split(', ').map(s => s.trim()))}
                          placeholder="PDF, DOCX, TXT"
                        />
                      </Field.Root>

                      <Field.Root>
                        <Field.Label>{t('createReview.officialUrlOptional')}</Field.Label>
                        <Input
                          value={config.official_url}
                          onChange={(e) => handleConfigChange('official_url', e.target.value)}
                          placeholder="https://example.com/standard"
                        />
                      </Field.Root>

                      <Field.Root>
                        <HStack gap={3}>
                          <Field.Label htmlFor="active-standard-upload" mb="0">
                            {t('createReview.activeStandard')}
                          </Field.Label>
                          <Switch.Root
                            id="active-standard-upload"
                            checked={Boolean(config.is_active)}
                            onCheckedChange={(details) => {
                              handleConfigChange('is_active', details.checked);
                            }}
                          >
                            <Switch.HiddenInput />
                            <Switch.Control />
                          </Switch.Root>
                        </HStack>
                        <Field.HelperText>
                          {t('createReview.inactiveStandardsHiddenFromUsers')}
                        </Field.HelperText>
                      </Field.Root>
                    </VStack>
                  </Accordion.ItemContent>
                </Accordion.Item>
              </Accordion.Root>

              {/* Upload Button */}
              <Button
                colorPalette="blue"
                size="lg"
                onClick={handleUpload}
                loading={isLoading}
                loadingText={t('createReview.uploading')}
                disabled={!selectedFile || !config.standard_id || !config.name}
              >
                <FiUpload /> {t('createReview.uploadMultiStandard')}
              </Button>

              {/* Info Alert */}
              <Alert.Root status="info">
                <Alert.Indicator />
                <Box>
                  <Alert.Title>{t('createReview.multiStandardUpload')}</Alert.Title>
                  <Alert.Description>
                    {t('createReview.multiStandardUploadInfo')}
                  </Alert.Description>
                </Box>
              </Alert.Root>
            </VStack>
                  </Tabs.Content>

                  {/* Standards Library Panel */}
                  <Tabs.Content value="library">
                    <VStack gap={4} align="stretch">
                      {/* Inline Edit Form - shown when editing */}
                      {editingStandard ? (
                        <Box>
                          <HStack mb={4}>
                            <Heading size="md">{t('createReview.editStandardName', { name: editingStandard.name })}</Heading>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => { setEditingStandard(null); }}
                            >
                              {t('createReview.backToLibrary')}
                            </Button>
                          </HStack>
                          <Card.Root variant="outline" p={4}>
                            <EditStandardForm 
                              standard={editingStandard} 
                              onUpdate={handleUpdateStandard}
                              onCancel={() => { setEditingStandard(null); }}
                              templates={templates}
                            />
                          </Card.Root>
                        </Box>
                      ) : existingStandards.length > 0 ? (
                        <>
                          <Heading size="md">{t('createReview.existingStandardsLibrary')}</Heading>
                          <Grid templateColumns="repeat(auto-fit, minmax(300px, 1fr))" gap={4}>
                            {existingStandards.map((standard) => (
                              <Card.Root key={standard.id} variant="outline" p={4}>
                                <VStack align="start" gap={2}>
                                  <HStack justify="space-between" w="full">
                                    <Text fontWeight="bold">{standard.name}</Text>
                                    <Badge colorPalette={standard.is_active ? "green" : "gray"}>
                                      {standard.is_active ? t('createReview.active') : t('createReview.inactive')}
                                    </Badge>
                                  </HStack>
                                  <Text fontSize="sm" color="gray.600">
                                    {t('createReview.typeVersion', { type: standard.standard_type, version: standard.version })}
                                  </Text>
                                  {standard.description && (
                                    <Text fontSize="sm" color="gray.600">
                                      {standard.description}
                                    </Text>
                                  )}
                                  <HStack gap={2}>
                                    <Badge colorPalette={standard.has_custom_prompt ? "blue" : "gray"} size="sm">
                                      {standard.has_custom_prompt ? t('createReview.customPrompt') : t('createReview.defaultPrompt')}
                                    </Badge>
                                    {standard.has_custom_prompt && (
                                      <Text fontSize="xs" color="gray.500">
                                        {t('createReview.charsCount', { count: standard.prompt_length })}
                                      </Text>
                                    )}
                                  </HStack>
                                  {standard.sections_count > 0 && (
                                    <Text fontSize="xs" color="gray.500">
                                      {t('createReview.sectionsCount', {
                                        count: standard.sections_count,
                                        unitPlural: getAssessmentUnitLabels(standard.standard_type, [], i18n.language).plural,
                                      })}
                                    </Text>
                                  )}
                                  <Text fontSize="xs" color="gray.500">
                                    {t('createReview.createdDate', { date: new Date(standard.created_at).toLocaleDateString() })}
                                  </Text>
                                  <HStack gap={2}>
                                    <Button
                                      size="sm"
                                      colorPalette="blue"
                                      variant="outline"
                                      onClick={() => handleEditStandard(standard)}
                                    >
                                      <FiSettings /> {t('createReview.editStandard')}
                                    </Button>
                                    <Button
                                      size="sm"
                                      colorPalette="red"
                                      variant="outline"
                                      onClick={() => handleDeleteStandard(standard)}
                                    >
                                      <FiTrash2 /> {t('createReview.delete')}
                                    </Button>
                                  </HStack>
                                </VStack>
                              </Card.Root>
                            ))}
                          </Grid>
                        </>
                      ) : (
                        <Box textAlign="center" py={8}>
                          <Text color="gray.500">{t('createReview.noStandardsFoundLibrary')}</Text>
                          <Text color="gray.400" fontSize="sm">{t('createReview.uploadFirstStandardGetStarted')}</Text>
                        </Box>
                      )}
                    </VStack>
                  </Tabs.Content>
              </Tabs.Root>
            </Card.Body>
          </Card.Root>

        {/* Template Info Modal */}
        <Button
          variant="outline"
          onClick={() => setIsOpen(true)}
          alignSelf="flex-start"
        >
          <FiInfo /> {t('createReview.viewTemplateExamples')}
        </Button>

        <DialogRoot open={isOpen} onOpenChange={(e) => setIsOpen(e.open)} size="xl">
          <DialogBackdrop />
          <Dialog.Positioner>
            <DialogContent maxH="80vh">
              <DialogHeader>{t('createReview.templateExamples')}</DialogHeader>
              <DialogCloseTrigger />
              <DialogBody pb={6} overflowY="auto">
                <VStack gap={4} align="stretch">
                  {Object.entries(templates).map(([key, template]) => (
                    <Card.Root key={key}>
                      <Card.Header>
                        <Heading size="sm">{template.name}</Heading>
                      </Card.Header>
                      <Card.Body>
                        <VStack gap={3} align="stretch">
                          <HStack>
                            <Badge colorPalette="blue">{template.category}</Badge>
                            <Badge colorPalette="green">{template.assessment_type}</Badge>
                          </HStack>
                          <Text fontSize="sm">{template.help_text}</Text>
                          <Text fontSize="sm" fontWeight="bold">{t('createReview.assessmentTypeLabel')}</Text>
                          <Text fontSize="sm">{getAssessmentTypeDescription(template.assessment_type)}</Text>
                          <Text fontSize="sm" fontWeight="bold">{t('createReview.fileRequirementsLabel')}</Text>
                          <Text fontSize="sm">{template.file_requirements?.join(', ') || t('createReview.noSpecificRequirements')}</Text>
                        </VStack>
                      </Card.Body>
                    </Card.Root>
                  ))}
                </VStack>
              </DialogBody>
            </DialogContent>
          </Dialog.Positioner>
        </DialogRoot>

        {/* Delete Standard Confirmation Modal */}
        <DialogRoot open={isDeleteModalOpen} onOpenChange={(e) => setIsDeleteModalOpen(e.open)} size="md">
          <DialogBackdrop />
          <Dialog.Positioner>
            <DialogContent>
              <DialogHeader>{t('createReview.deleteStandard')}</DialogHeader>
              <DialogCloseTrigger />
              <DialogBody pb={6}>
                <VStack gap={4} align="stretch">
                  <Alert.Root status="warning">
                    <Alert.Indicator />
                    <Box>
                      <Alert.Title>{t('createReview.warning')}</Alert.Title>
                      <Alert.Description>
                        {t('createReview.confirmDeleteStandard', { name: deletingStandard?.name })}
                      </Alert.Description>
                    </Box>
                  </Alert.Root>
                  <Text>
                    {t('createReview.permanentlyRemoveStandardData')}
                  </Text>
                </VStack>
              </DialogBody>
              <DialogFooter>
                <HStack gap={3}>
                  <Button variant="ghost" onClick={() => setIsDeleteModalOpen(false)}>
                    {t('createReview.cancel')}
                  </Button>
                  <Button colorPalette="red" onClick={confirmDeleteStandard}>
                    {t('createReview.deleteStandard')}
                  </Button>
                </HStack>
              </DialogFooter>
            </DialogContent>
          </Dialog.Positioner>
        </DialogRoot>
      </VStack>
    </Box>
  );
};

export default MultiStandardUpload; 
